import { Box, Button, Flex, Heading, Stack, Text } from '@everlywell/ui-kit';
import { AnimatePresence, motion, LayoutGroup } from 'framer-motion';
import { Link } from 'gatsby';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useState } from 'react';

type Measure =
  | 'BPD'
  | 'CBP'
  | 'CHL'
  | 'COA'
  | 'COL'
  | 'GSD'
  | 'KED'
  | 'SSD'
  | 'SMD'
  | 'SMC';

export interface HealthTest {
  id: string;
  title: string;
  image: {
    gatsbyImageData: IGatsbyImageData;
  };
  measures: Measure[];
  description: string;
  price: string;
  conditions: string[];
}

interface FilterableMeasuresProps {
  tests: HealthTest[];
}

const FilterableMeasures: React.FC<FilterableMeasuresProps> = ({ tests }) => {
  const [selectedMeasures, setSelectedMeasures] = useState<Measure[]>([]);

  const allMeasures: Measure[] = [
    'BPD',
    'CBP',
    'CHL',
    'COA',
    'COL',
    'GSD',
    'KED',
    'SSD',
    'SMD',
    'SMC',
  ];

  const handleMeasureSelect = (measure: Measure) => {
    setSelectedMeasures((prev) =>
      prev.includes(measure)
        ? prev.filter((m) => m !== measure)
        : [...prev, measure],
    );
  };

  const resetFilters = () => {
    setSelectedMeasures([]);
  };

  const isMeasureSelected = (measure: Measure) =>
    selectedMeasures.includes(measure);

  const filteredTests = tests.filter(
    (test) =>
      selectedMeasures.length === 0 ||
      test.measures.some((measure) => selectedMeasures.includes(measure)),
  );

  return (
    <Box maxWidth="976px" mx="auto" mt={10}>
      <Box
        bg="#E8E8E2"
        p={4}
        mb={3}
        borderRadius="md"
        display="flex"
        flexDirection={['column', 'row', 'row']}
        justifyContent="space-between"
        alignItems="center"
      >
        <Text color="#0F0F0F" mb={[3, 0, 0]}>
          Select HEDIS Measure:
        </Text>
        <Flex gap={2} flexWrap="wrap" alignItems="center">
          <Button
            onClick={resetFilters}
            fontSize="15px"
            backgroundColor={
              selectedMeasures.length === 0 ? '#FFFFFF' : '#F2F2ED'
            }
            borderRadius="10px"
            color="#0F0F0F"
            py={2}
            px={4}
            _hover={{
              backgroundColor: '#FFFFFF',
            }}
          >
            Show all
          </Button>
          {allMeasures.map((measure) => (
            <Button
              key={measure}
              fontSize="15px"
              backgroundColor={
                isMeasureSelected(measure) ? '#FFFFFF' : '#F2F2ED'
              }
              color="#0F0F0F"
              py={2}
              px={2}
              borderRadius="10px"
              onClick={() => handleMeasureSelect(measure)}
              _hover={{
                backgroundColor: '#FFFFFF',
              }}
            >
              {measure}
            </Button>
          ))}
        </Flex>
      </Box>

      <Stack spacing={2}>
        <LayoutGroup>
          <AnimatePresence mode="popLayout">
            {filteredTests.map((test) => (
              <motion.div
                key={test.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transition: {
                    duration: 0.4,
                    ease: 'easeOut',
                  },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  transition: {
                    duration: 0.3,
                    ease: 'easeIn',
                  },
                }}
              >
                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  backgroundColor="white"
                  mb={2}
                >
                  <Flex
                    justifyContent="space-between"
                    flexDirection={['column', 'row', 'row']}
                    alignItems={['flex-start', 'center', 'center']}
                  >
                    <Flex gap={4}>
                      <GatsbyImage
                        image={getImage(test.image.gatsbyImageData)!}
                        alt=""
                        style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '12px',
                        }}
                      />
                      <Box>
                        <Heading size="md" mb={2}>
                          {test.title}
                        </Heading>
                        <Flex gap={2}>
                          {test.measures.map((measure) => (
                            <Box
                              key={measure}
                              bg="#E8E8E2"
                              px={3}
                              py={1.5}
                              borderRadius="full"
                              color="#545454"
                              fontSize="14px"
                            >
                              {measure}
                            </Box>
                          ))}
                        </Flex>
                      </Box>
                    </Flex>
                    <Button
                      as={Link}
                      to="/contact-us"
                      backgroundColor="#F9F9F6"
                      color="#0F0F0F"
                      fontSize="16px"
                      fontWeight="500"
                      py={2}
                      px={6}
                      mt={[3, 0, 0]}
                      borderRadius="full"
                      _hover={{
                        backgroundColor: '#F9F9F6',
                      }}
                    >
                      Learn more
                    </Button>
                  </Flex>
                </Box>
              </motion.div>
            ))}
          </AnimatePresence>
        </LayoutGroup>
      </Stack>
    </Box>
  );
};

export default FilterableMeasures;
